import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AlertCircle, Check, Clock, Loader2 } from 'lucide-react';
import { useLCATProgress } from '../../../hooks/useLCATProgress';
function StepIcon({ status }) {
    switch(status){
        case 'completed':
            return /*#__PURE__*/ _jsx(Check, {
                className: "h-4 w-4 text-green-500"
            });
        case 'in_progress':
        case 'started':
            return /*#__PURE__*/ _jsx(Loader2, {
                className: "h-4 w-4 text-blue-500 animate-spin"
            });
        case 'error':
            return /*#__PURE__*/ _jsx(AlertCircle, {
                className: "h-4 w-4 text-red-500"
            });
        default:
            return /*#__PURE__*/ _jsx(Clock, {
                className: "h-4 w-4 text-gray-400"
            });
    }
}
function formatTimestamp(timestamp) {
    if (!timestamp) return '';
    try {
        const date = new Date(timestamp);
        return date.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    } catch  {
        return '';
    }
}
function StepRow({ step }) {
    const showTimestamp = step.status === 'completed' && step.timestamp;
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-start gap-3 py-2",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex-shrink-0 mt-0.5",
                children: /*#__PURE__*/ _jsx(StepIcon, {
                    status: step.status
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex-1 min-w-0",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-sm font-medium text-foreground",
                                children: step.label
                            }),
                            step.progress !== undefined && step.status === 'in_progress' && /*#__PURE__*/ _jsxs("span", {
                                className: "text-xs text-muted-foreground",
                                children: [
                                    "(",
                                    step.progress,
                                    "%)"
                                ]
                            }),
                            showTimestamp && /*#__PURE__*/ _jsx("span", {
                                className: "text-xs text-green-600 dark:text-green-400",
                                children: formatTimestamp(step.timestamp)
                            })
                        ]
                    }),
                    step.details && /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-muted-foreground mt-0.5 break-words",
                        children: step.details
                    })
                ]
            })
        ]
    });
}
export function LCATProgressView({ messageId, lcatRunId }) {
    const { steps, hasStarted, hasError, isComplete, completionMessage, errorMessage } = useLCATProgress(messageId, lcatRunId);
    if (!hasStarted) {
        return null;
    }
    const completedCount = steps.filter((s)=>s.status === 'completed').length;
    const totalCount = steps.length;
    const progressPercent = completedCount / totalCount * 100;
    return /*#__PURE__*/ _jsxs("div", {
        className: "w-full border border-border rounded-lg overflow-hidden bg-card",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "px-4 py-3 border-b border-border bg-muted/50",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-between",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2",
                                children: [
                                    hasError ? /*#__PURE__*/ _jsx(AlertCircle, {
                                        className: "h-5 w-5 text-red-500"
                                    }) : isComplete ? /*#__PURE__*/ _jsx(Check, {
                                        className: "h-5 w-5 text-green-500"
                                    }) : /*#__PURE__*/ _jsx(Loader2, {
                                        className: "h-5 w-5 text-blue-500 animate-spin"
                                    }),
                                    /*#__PURE__*/ _jsx("h3", {
                                        className: "text-sm font-semibold text-foreground",
                                        children: "LCAT Analysis Progress"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "text-xs text-muted-foreground",
                                children: [
                                    completedCount,
                                    "/",
                                    totalCount,
                                    " steps"
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "mt-2 w-full bg-muted rounded-full h-1.5",
                        children: /*#__PURE__*/ _jsx("div", {
                            className: `h-1.5 rounded-full transition-all duration-300 ${hasError ? 'bg-red-500' : isComplete ? 'bg-green-500' : 'bg-blue-500'}`,
                            style: {
                                width: `${progressPercent}%`
                            }
                        })
                    }),
                    isComplete && completionMessage && /*#__PURE__*/ _jsx("div", {
                        className: "mt-2 text-xs text-green-600 dark:text-green-400",
                        children: completionMessage
                    }),
                    hasError && errorMessage && /*#__PURE__*/ _jsx("div", {
                        className: "mt-2 text-xs text-red-600 dark:text-red-400",
                        children: errorMessage
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "px-4 py-3 space-y-1",
                children: steps.map((step)=>/*#__PURE__*/ _jsx(StepRow, {
                        step: step
                    }, step.step))
            })
        ]
    });
}
